import React, { useEffect, useState } from "react";
import { Allocation, CoreTeam } from "../data/types";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { ChevronDownIcon, XIcon } from "../icons";
import { ConfirmationModal } from "./ConfirmationModal";
import { ConfirmationModalMeta } from "../data";
import { API_BASE_URL } from "../data";

type Props = {
    meta: any;
    isOpen: boolean;
    allocations: Allocation[];
    coreTeams?: CoreTeam[];
    onCancel: () => void;
    title: string;
    message: string;
    onAllocationRemoved: (allocationId: number, volunteerId: number) => void;
};

export const VolunteerShiftViewModal = ({
    meta,
    allocations,
    coreTeams = [],
    isOpen,
    onCancel,
    title,
    message,
    onAllocationRemoved,
}: Props) => {
    const [error, setError] = useState<string | null>(null);
    const [isDeallocateModalOpen, setIsDeallocateModalOpen] = useState(false);
    const [deAllocateLoading, setDeAllocateLoading] = useState(false);
    const [selectedAllocation, setSelectedAllocation] = useState<Allocation | null>(null);

    // Separate state for different tables
    const [shiftAllocations, setShiftAllocations] = useState<Allocation[]>([]);
    const [leadRoles, setLeadRoles] = useState<Allocation[]>([]);
    const [showExcludedSections, setShowExcludedSections] = useState(false);

    useEffect(() => {
        if (allocations) {
            // Split allocations into Roles (id: -1) and Shifts (id > 0)
            const roles = allocations.filter(a => a.id === -1);
            const shifts = allocations.filter(a => a.id !== -1);

            setLeadRoles(roles);
            setShiftAllocations(shifts);
        }
    }, [allocations]);

    useEffect(() => {
        if (!isOpen) return;
        // Only error if ALL lists are empty
        const hasAllocations = allocations && allocations.length > 0;
        const hasCoreTeams = coreTeams && coreTeams.length > 0;
        if (!hasAllocations && !hasCoreTeams) {
            setError("No allocation information available.");
        } else {
            setError(null);
        }
    }, [isOpen, allocations, coreTeams]);

    const columns = meta.columns ?? [];

    const Tag = ({ value = null, children }: any) => {
        const variantClasses: Record<string, string> = {
            Accepted: "bg-success-600 text-white",
            Pending: "bg-warn-400 text-gray-700",
            default: "bg-primary-600 text-white",
        };
        const classes = variantClasses[value] ?? variantClasses.default;
        return (
            <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full ${classes} text-xs font-medium whitespace-nowrap`}>
                {children}
            </span>
        )
    }

    const renderCell = (item: Allocation, column: any) => {
        switch (column.key) {
            case "event_name":
                return item?.shift?.area?.event?.name;
            case "area_name":
                return item?.shift?.area?.name;
            case "shift_duty_name":
                // If it's a role, show the duty name (e.g. "Event Lead") instead of "All Shifts"
                if (item.id === -1) return item?.shift?.duty?.name;
                return item?.shift?.title || item?.shift?.duty?.name;
            case "status":
                // Synthetic rows are always accepted
                if (item.id === -1) return <Tag value="Accepted">Accepted</Tag>;

                const status = item?.allocation_times?.[0]?.status;
                const statusText = status === true ? "Accepted" : "Pending";
                return <Tag value={statusText}>{statusText}</Tag>;
            case "start_time":
                return item.id === -1 ? "N/A" : formatTime(item?.allocation_times?.[0]?.start_time);
            case "end_time":
                return item.id === -1 ? "N/A" : formatTime(item?.allocation_times?.[0]?.end_time);
            case "action":
                // Disable deallocate for synthetic rows (Lead roles)
                if (item.id === -1) {
                    return <span className="text-gray-400 italic text-xs">Role Assignment</span>;
                }
                return (
                    <Button
                        type='button'
                        color="danger"
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveAllocationClick(item)}
                    >
                        <XIcon className="w-4 h-4 me-1" />
                        Deallocate
                    </Button>
                );
            default:
                return "—";
        }
    };

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return 'N/A';
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    }

    const handleRemoveAllocationClick = (allocation: Allocation) => {
        setSelectedAllocation(allocation);
        setIsDeallocateModalOpen(true);
    }

    const handleRemoveAllocationClickClose = () => {
        setSelectedAllocation(null);
        setIsDeallocateModalOpen(false);
    }

    const handleRemoveAllocation = async () => {
        setDeAllocateLoading(true);
        try {
            if (!selectedAllocation) throw new Error("No Allocation Selected");

            const response = await fetch(`${API_BASE_URL}/allocation/shift/${selectedAllocation.shift_id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    volunteer: [selectedAllocation.volunteer_id],
                })
            });

            if (!response.ok) throw new Error("Failed to deallocate volunteer.");

            // Update only the shifts list, leads cannot be removed here
            setShiftAllocations(prev => prev.filter(a => a.id !== selectedAllocation.id));
            onAllocationRemoved(selectedAllocation.id, selectedAllocation.volunteer_id);
            handleRemoveAllocationClickClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to deallocate volunteer.");
        }
        finally {
            setDeAllocateLoading(false);
        }
    }

    // Helper to render table (reused for both sections)
    const renderTable = (data: Allocation[], title: string, isExcluded: boolean = false) => {
        if (!data || data.length === 0) return null;
        return (
            <div className="mb-6">
                <h3 className={`text-sm font-bold mb-2 px-6 uppercase tracking-wider ${isExcluded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {title}
                </h3>
                {isExcluded && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 px-6 mb-2">
                        These allocations are from events not included in duplicate checks.
                    </p>
                )}
                <div className="px-6 overflow-x-auto">
                    <table className={`w-full border-collapse table-auto text-sm dark:text-gray-300 ${isExcluded ? 'opacity-75' : ''}`}>
                        <thead className={isExcluded ? "bg-gray-100 dark:bg-gray-700" : "bg-gray-100 dark:bg-gray-700"}>
                            <tr>
                                {columns.map((column: any) => (
                                    <th key={column.key} className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {column.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((allocation, index) => (
                                <tr key={index} className={isExcluded ? "bg-gray-50 dark:bg-gray-700" : "odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700"}>
                                    {columns.map((column: any) => (
                                        <td key={column.key} className="px-3 py-2 border border-gray-600 align-top">
                                            {renderCell(allocation, column)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Helper to render leadership roles table (custom columns without status/times)
    const renderLeadRolesTable = (data: Allocation[], title: string, isExcluded: boolean = false) => {
        if (!data || data.length === 0) return null;

        // Custom columns for leadership roles: Event, Area, Shift, Role
        const leadRoleColumns = [
            { key: 'event_name', header: 'Event' },
            { key: 'area_name', header: 'Area' },
            { key: 'shift_name', header: 'Shift' },
            { key: 'role_type', header: 'Role' },
        ];

        const renderLeadCell = (item: Allocation, columnKey: string) => {
            switch (columnKey) {
                case 'event_name':
                    return item?.shift?.area?.event?.name || 'N/A';
                case 'area_name':
                    return item?.shift?.area?.name || 'N/A';
                case 'shift_name':
                    return item?.shift?.title || 'N/A';
                case 'role_type':
                    // duty.name contains the role type (e.g., "Event Lead", "Area Lead", "Shift Lead", "Shift Deputy Lead")
                    return item?.shift?.duty?.name || 'N/A';
                default:
                    return '—';
            }
        };

        return (
            <div className="mb-6">
                <h3 className={`text-sm font-bold mb-2 px-6 uppercase tracking-wider ${isExcluded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {title}
                </h3>
                {isExcluded && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 px-6 mb-2">
                        These leadership roles are from events not included in duplicate checks.
                    </p>
                )}
                <div className="px-6 overflow-x-auto">
                    <table className={`w-full border-collapse table-auto text-sm dark:text-gray-300 ${isExcluded ? 'opacity-75' : ''}`}>
                        <thead className="bg-gray-100 dark:bg-gray-700">
                            <tr>
                                {leadRoleColumns.map((column) => (
                                    <th key={column.key} className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {column.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((allocation, index) => (
                                <tr key={index} className={isExcluded ? "bg-gray-50 dark:bg-gray-700" : "odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700"}>
                                    {leadRoleColumns.map((column) => (
                                        <td key={column.key} className="px-3 py-2 border border-gray-600 align-top">
                                            {renderLeadCell(allocation, column.key)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onCancel}
                title={title}
                showCloseButton={false}
                maxWidth="5xl"
                footer={
                    <div className="flex justify-end">
                        <Button onClick={onCancel} variant="outline" color="neutral">
                            Close
                        </Button>
                    </div>
                }
            >
                {error ? (
                    <div className="m-6 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="px-6 py-3">
                            <p className="text-md text-gray-500 dark:text-gray-400">{message}</p>
                        </div>

                        {/* 1. Leadership Roles Table */}
                        {(() => {
                            const countedLeadRoles = leadRoles.filter(
                                a => a.shift?.area?.event?.include_in_duplicate_checks !== false
                            );
                            return renderLeadRolesTable(countedLeadRoles, "Leadership Roles");
                        })()}

                        {/* 2. Core Team Assignments */}
                        {coreTeams && coreTeams.length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-sm font-bold mb-2 px-6 uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                    Core Team Assignments ({coreTeams.length})
                                </h3>
                                <div className="px-6 overflow-x-auto">
                                    <table className="w-full border-collapse table-auto text-sm dark:text-gray-300">
                                        <thead className="bg-gray-100 dark:bg-gray-700">
                                            <tr>
                                                <th className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                                    Stream(s)
                                                </th>
                                                <th className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                                    Role
                                                </th>
                                                <th className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                                    Tier
                                                </th>
                                                <th className="px-3 py-2 border border-gray-600 text-start font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                                    Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {coreTeams.map((ct, index) => (
                                                <tr key={ct.id || index} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700">
                                                    <td className="px-3 py-2 border border-gray-600 align-top">
                                                        {ct.streams?.join(', ') || 'N/A'}
                                                    </td>
                                                    <td className="px-3 py-2 border border-gray-600 align-top">
                                                        {ct.role || 'N/A'}
                                                    </td>
                                                    <td className="px-3 py-2 border border-gray-600 align-top">
                                                        {ct.tier || 'N/A'}
                                                    </td>
                                                    <td className="px-3 py-2 border border-gray-600 align-top">
                                                        <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                                            ct.approved
                                                                ? 'bg-success-600 text-white'
                                                                : 'bg-warn-400 text-gray-700'
                                                        }`}>
                                                            {ct.approved ? 'Approved' : 'Pending'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* 3. Standard Shift Allocations - Split by include_in_duplicate_checks */}
                        {(() => {
                            const countedAllocations = shiftAllocations.filter(
                                a => a.shift?.area?.event?.include_in_duplicate_checks !== false
                            );
                            const excludedAllocations = shiftAllocations.filter(
                                a => a.shift?.area?.event?.include_in_duplicate_checks === false
                            );
                            const excludedLeadRoles = leadRoles.filter(
                                a => a.shift?.area?.event?.include_in_duplicate_checks === false
                            );
                            const totalExcluded = excludedAllocations.length + excludedLeadRoles.length;

                            return (
                                <>
                                    {/* Counted Shift Allocations */}
                                    {renderTable(countedAllocations, "Shift Allocations")}

                                    {/* Excluded Items (Collapsible) - includes both allocations and leadership roles */}
                                    {totalExcluded > 0 && (
                                        <div className="px-6 border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                                            <button
                                                type="button"
                                                onClick={() => setShowExcludedSections(!showExcludedSections)}
                                                className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-3"
                                            >
                                                <ChevronDownIcon className={`w-5 h-5 transition-transform ${showExcludedSections ? 'rotate-0' : '-rotate-90'}`} />
                                                <span className="text-sm font-medium">
                                                    Show Excluded Items ({totalExcluded})
                                                </span>
                                            </button>

                                            {showExcludedSections && (
                                                <div className="-mx-6">
                                                    {renderLeadRolesTable(excludedLeadRoles, "Excluded Leadership Roles", true)}
                                                    {renderTable(excludedAllocations, "Excluded Shift Allocations", true)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })()}

                        {/* Fallback if all are empty but no error was triggered */}
                        {leadRoles.length === 0 && shiftAllocations.length === 0 && (!coreTeams || coreTeams.length === 0) && (
                             <div className="px-6 text-gray-500 italic">No allocations found.</div>
                        )}
                    </>
                )}
            </Modal>

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeallocateModalOpen}
                onConfirm={handleRemoveAllocation}
                onCancel={handleRemoveAllocationClickClose}
                title={"Deallocate Volunteer from the shift?"}
                message={"This action will remove the volunteer from this shift. This action cannot be undone."}
                isLoading={deAllocateLoading}
                confirmButtonText="Deallocate"
                loadingButtonText="Deallocating..."
            />
        </>
    );
};
